/**
 * KanbanBoard component - main board with drag and drop for tasks and columns
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { clsx } from 'clsx';
import KanbanColumn from './KanbanColumn';
import SortableColumn from './SortableColumn';
import TaskCard from './TaskCard';
import { useBoardStore } from '@stores/boardStore';
import { toast } from '@components/ui/Toast';
import { isApiError } from '@services/api/client';
import type { Task, BoardColumn } from '@services/api';

interface KanbanBoardProps {
  onTaskClick: (taskId: string) => void;
  onAddTask: (columnId: string) => void;
  onAddColumn?: () => void;
  onColumnSettings?: (columnId: string) => void;
  searchQuery?: string;
  filterPriority?: string;
  filterColumnId?: string;
}

export default function KanbanBoard({
  onTaskClick,
  onAddTask,
  onAddColumn,
  onColumnSettings,
  searchQuery = '',
  filterPriority = '',
  filterColumnId = '',
}: KanbanBoardProps) {
  // Subscribe to individual store slices (not the whole store) so unrelated
  // mutations (isLoading, error, selectedTaskId, polling updates to other
  // fields) do not re-render the entire board tree. Zustand actions are stable.
  const currentBoard = useBoardStore((s) => s.currentBoard);
  const tasks = useBoardStore((s) => s.tasks);
  const columnTaskIds = useBoardStore((s) => s.columnTaskIds);
  const moveTask = useBoardStore((s) => s.moveTask);
  const reorderColumns = useBoardStore((s) => s.reorderColumns);
  const fetchBoardTasks = useBoardStore((s) => s.fetchBoardTasks);

  const { t } = useTranslation();
  const [_activeId, setActiveId] = useState<string | null>(null);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [activeColumn, setActiveColumn] = useState<BoardColumn | null>(null);

  // Filter tasks by search query and filters
  const filterTask = useCallback((task: Task): boolean => {
    // Filter by priority
    if (filterPriority && task.priority !== filterPriority) return false;
    // Filter by column
    if (filterColumnId && task.columnId !== filterColumnId) return false;
    // Filter by search query
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      task.title.toLowerCase().includes(query) ||
      (task.description?.toLowerCase().includes(query) ?? false) ||
      (task.taskKey?.toLowerCase().includes(query) ?? false) ||
      (task.labels?.some(label => label.name.toLowerCase().includes(query)) ?? false)
    );
  }, [searchQuery, filterPriority, filterColumnId]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    // Touch devices need a press-and-hold delay so a plain vertical swipe
    // scrolls the column instead of accidentally starting a drag.
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Custom collision detection that handles both column and task dragging
  const collisionDetection: CollisionDetection = useCallback((args) => {
    const { active } = args;
    const isColumnDrag = active.data?.current?.type === 'sortable-column';

    if (isColumnDrag) {
      // For column dragging, only detect collisions with other sortable columns
      // Use rectIntersection for more forgiving column-to-column detection
      const collisions = rectIntersection(args);
      return collisions.filter(
        (collision) => collision.data?.droppableContainer?.data?.current?.type === 'sortable-column'
      );
    }

    // For task dragging, use pointerWithin for precise drop detection
    // This works better for dropping tasks into columns and between tasks
    const pointerCollisions = pointerWithin(args);

    // If we have pointer collisions, prefer those
    if (pointerCollisions.length > 0) {
      // Prioritize task collisions over column collisions for better insertion
      const taskCollisions = pointerCollisions.filter(
        (c) => !c.data?.droppableContainer?.data?.current?.type?.includes('column')
      );
      if (taskCollisions.length > 0) {
        return taskCollisions;
      }
      return pointerCollisions;
    }

    // Fall back to rectIntersection for edge cases
    return rectIntersection(args);
  }, []);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      const id = active.id as string;
      setActiveId(id);

      // Check if dragging a column or a task
      if (active.data?.current?.type === 'sortable-column') {
        // Extract actual column ID from prefixed sortable ID
        const columnId = active.data.current.columnId as string;
        const column = currentBoard?.columns.find((c) => c.id === columnId);
        setActiveColumn(column || null);
        setActiveTask(null);
      } else {
        setActiveTask(tasks[id] || null);
        setActiveColumn(null);
      }
    },
    [tasks, currentBoard?.columns]
  );

  const handleDragOver = useCallback((_event: DragOverEvent) => {
    // We handle positioning in dragEnd
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveId(null);
      setActiveTask(null);
      setActiveColumn(null);

      if (!over || !currentBoard) return;

      // Handle column reordering
      if (active.data?.current?.type === 'sortable-column') {
        // Extract actual column IDs from prefixed sortable IDs
        const activeColumnId = active.data.current.columnId as string;
        const overData = over.data?.current;
        // over could be a sortable-column or a droppable column
        const overColumnId = overData?.type === 'sortable-column'
          ? (overData.columnId as string)
          : (over.id as string);

        if (activeColumnId !== overColumnId) {
          const columns = [...currentBoard.columns].sort(
            (a, b) => a.position - b.position
          );
          const oldIndex = columns.findIndex((c) => c.id === activeColumnId);
          const newIndex = columns.findIndex((c) => c.id === overColumnId);

          if (oldIndex !== -1 && newIndex !== -1) {
            // Create new order
            const newColumns = [...columns];
            const removed = newColumns.splice(oldIndex, 1)[0];
            if (!removed) return;
            newColumns.splice(newIndex, 0, removed);

            const newOrder = newColumns.map((c) => c.id);

            try {
              await reorderColumns(currentBoard.id, newOrder);
              toast.success('Column order updated');
            } catch (error) {
              console.error('Failed to reorder columns:', error);
              toast.error('Failed to reorder columns');
            }
          }
        }
        return;
      }

      // Handle task movement
      const activeTaskId = active.id as string;
      const task = tasks[activeTaskId];
      if (!task) return;

      // Determine target column and position
      let targetColumnId: string;
      let targetPosition: number;

      const overData = over.data?.current;

      if (overData?.type === 'column' || overData?.type === 'sortable-column') {
        // Dropped on a column's empty area (the inner droppable or the
        // sortable-column wrapper) - append to the end of that column.
        targetColumnId =
          overData.type === 'sortable-column'
            ? (overData.columnId as string)
            : (over.id as string);
        const columnTasks = columnTaskIds[targetColumnId] || [];

        if (task.columnId === targetColumnId) {
          // Same column: moveTask removes the card first, so the last valid
          // index is length - 1 (the array still includes the dragged card).
          targetPosition = Math.max(0, columnTasks.length - 1);
          // No-op when the card is already the last one in its column.
          if (columnTasks.indexOf(activeTaskId) === targetPosition) return;
        } else {
          targetPosition = columnTasks.length;
        }
      } else {
        // Dropped on another task
        const overTaskId = over.id as string;
        const overTask = tasks[overTaskId];
        if (!overTask) return;

        targetColumnId = overTask.columnId;
        const columnTasks = columnTaskIds[targetColumnId] || [];
        const overIndex = columnTasks.indexOf(overTaskId);

        if (task.columnId === targetColumnId) {
          // Same column - reorder. moveTask removes the card before
          // re-inserting, so the target index is simply the over index for
          // both upward and downward moves.
          const activeIndex = columnTasks.indexOf(activeTaskId);
          // No-op only when the card is dropped back onto its own slot.
          // Derived from the live array index rather than the stored
          // position, which is not renumbered for the other cards and can be
          // stale after a previous reorder.
          if (activeIndex === overIndex) return;
          targetPosition = overIndex;
        } else {
          // Different column - insert at position
          targetPosition = overIndex;
        }
      }

      // Move the task with optimistic update
      try {
        await moveTask(activeTaskId, targetColumnId, targetPosition);
      } catch (error) {
        console.error('Failed to move task:', error);

        // Handle conflict errors specifically
        if (isApiError(error)) {
          if (error.status === 409 || error.code === 'CONFLICT') {
            toast.warning(
              'This task was modified by another user. The board has been refreshed.'
            );
            // Refetch to get the latest state
            if (currentBoard?.id) {
              void fetchBoardTasks(currentBoard.id);
            }
          } else {
            toast.error(error.message || 'Failed to move task');
          }
        } else {
          toast.error('Failed to move task. Please try again.');
        }
        // Note: Optimistic rollback is handled by the store
      }
    },
    [tasks, columnTaskIds, moveTask, reorderColumns, currentBoard, fetchBoardTasks]
  );

  if (!currentBoard) {
    return null;
  }

  // Create a copy before sorting to avoid mutating state
  const columns = [...currentBoard.columns].sort((a, b) => a.position - b.position);
  // Use prefixed IDs for sortable column context to avoid collision with droppable column IDs
  const sortableColumnIds = columns.map((c) => `sortable-column-${c.id}`);
  const viewSettings = currentBoard.viewSettings;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div
        className={clsx(
          'flex h-full gap-4 overflow-x-auto p-4',
          'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-300',
          'dark:scrollbar-thumb-neutral-700'
        )}
      >
        <SortableContext items={sortableColumnIds} strategy={horizontalListSortingStrategy}>
          {columns.map((column) => {
            const allColumnTasks = (columnTaskIds[column.id] || [])
              .map((id) => tasks[id])
              .filter((t): t is Task => t !== undefined);
            const columnTasks = allColumnTasks.filter(filterTask);

            const columnProps = {
              column,
              tasks: columnTasks,
              // Unfiltered count so WIP limits/badges stay correct under filters
              totalCount: allColumnTasks.length,
              onTaskClick,
              onAddTask: () => onAddTask(column.id),
              showLabels: viewSettings?.showLabels ?? true,
              showAssignee: viewSettings?.showAssignee ?? true,
              showDueDate: viewSettings?.showDueDate ?? true,
              showPriority: viewSettings?.showPriority ?? true,
              showSubtaskProgress: viewSettings?.showSubtaskProgress ?? true,
              showAttachments: viewSettings?.showAttachments ?? true,
              compact: viewSettings?.compactMode ?? false,
            };

            return (
              <SortableColumn key={column.id} id={column.id}>
                {onColumnSettings ? (
                  <KanbanColumn
                    {...columnProps}
                    onColumnSettings={() => onColumnSettings(column.id)}
                  />
                ) : (
                  <KanbanColumn {...columnProps} />
                )}
              </SortableColumn>
            );
          })}
        </SortableContext>

        {/* Add column button */}
        <div className="flex-shrink-0">
          <button
            onClick={onAddColumn}
            className={clsx(
              'flex h-12 w-52 items-center justify-center gap-2 rounded-lg',
              'border border-dashed border-neutral-300 text-neutral-500',
              'transition-colors hover:border-neutral-400 hover:text-neutral-600',
              'dark:border-neutral-700 dark:text-neutral-500',
              'dark:hover:border-neutral-600 dark:hover:text-neutral-400'
            )}
          >
            <span className="text-lg">+</span>
            <span>{t('board.addColumn', 'Add column')}</span>
          </button>
        </div>
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {activeTask ? (
          <div className="rotate-2 transform">
            <TaskCard
              task={activeTask}
              showLabels={viewSettings?.showLabels ?? true}
              showAssignee={viewSettings?.showAssignee ?? true}
              showDueDate={viewSettings?.showDueDate ?? true}
              showPriority={viewSettings?.showPriority ?? true}
              showSubtaskProgress={viewSettings?.showSubtaskProgress ?? true}
              showAttachments={viewSettings?.showAttachments ?? true}
              compact={viewSettings?.compactMode ?? false}
            />
          </div>
        ) : null}
        {activeColumn ? (
          <div className="rotate-1 transform opacity-80">
            <div
              className={clsx(
                'flex h-32 w-72 flex-col rounded-lg',
                'bg-neutral-100 shadow-xl dark:bg-neutral-900/90'
              )}
            >
              <div className="flex items-center gap-2 p-3">
                {activeColumn.color && (
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: activeColumn.color }}
                  />
                )}
                <h3 className="font-semibold text-neutral-700 dark:text-neutral-200">
                  {activeColumn.name}
                </h3>
              </div>
              <div className="flex-1 rounded-b-lg bg-neutral-200/50 dark:bg-neutral-800/50" />
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
